import { useCanWrite } from '@hooks/useCanWrite';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Employee } from '@models/employee.model';
import { UserRoles, UserOrganizationRoleStatus } from '@models/user.model';
import { SecondarySidebar } from '@components/organisms/SecondarySidebar';
import { getVisibleUserRoleTabs } from './constants/constants';
import { useEmployees } from '@hooks/api/employees/useEmployees';
import { Input } from '@components/atoms/Input';
import { AccountCircleOutlined, Search } from '@mui/icons-material';
import { UserCard } from './components/UserCard';
import { Loading } from '@components/atoms/Loading';
import { EmptyData } from '@components/molecules/EmptyData';
import AddIcon from '@mui/icons-material/Add';
import { CreateEditEmployeeModal } from './components/UserCard/CreateEditEmployeeModal';
import { SeatsIndicator } from './components/SeatsIndicator';
import { Pagination } from '@components/molecules/Pagination';
import { Button } from '@components/atoms/Button';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { AddAddonModal } from './components/AddAddonModal';
import { useSubscription, useActiveSeats } from '@hooks/api/subscription';
import { useTotalIncludedSeats } from '@hooks/api/subscription/useTotalIncludedSeats';
import { Modal } from '@components/molecules/Modal';
import ChangeUserStatusModal from '@components/organisms/user/ChangeUserStatusModal';
import { useUpdateUserOrganizationStatus } from '@hooks/api/users';
import ReactSelect from '@components/atoms/ReactSelect';
import UserPunchHistoryDrawer from '@components/organisms/UserPunchHistoryDrawer';
import { SeatUsageModal } from './components/SeatUsageModal';
import { isNoSeatsAvailableError, getNoSeatsExtensions } from '@utils/errorMessage';
import { useTranslation } from '@hooks/useTranslation';

interface RouteParams {
  id: string;
  role: UserRoles;
}

const UsersPage: React.FC = () => {
  const tActionMessages = useTranslation('actionMessages');
  const [showNewEmployeeModal, setShowNewEmployeeModal] =
    useState<boolean>(false);
  const [showAddAddonModal, setShowAddAddonModal] = useState<boolean>(false);
  const [showNoSeatsModal, setShowNoSeatsModal] = useState<boolean>(false);
  const [showSeatModal, setShowSeatModal] = useState<boolean>(false);
  const [showChangeStatusModal, setShowChangeStatusModal] =
    useState<boolean>(false);
  const [initialData, setInitialData] = useState<Employee | null>(null);
  const [statusEmployee, setStatusEmployee] = useState<Employee | null>(null);
  const [historyEmployee, setHistoryEmployee] = useState<Employee | null>(null);
  const [selectedStatus, setSelectedStatus] =
    useState<UserOrganizationRoleStatus | null>(null);
  const [page, setPage] = useState<number>(1);
  // Sprint 20, story-093: populated when the server refuses a mutation with
  // NO_SEATS_AVAILABLE (the pre-check was stale or bypassed), so the fallback
  // AddAddonModal instance can show the exact counts without a second round trip.
  const [seatErrorInfo, setSeatErrorInfo] = useState<{
    occupiedSeats: number;
    includedSeats: number;
  } | null>(null);

  const [statusFilter, setStatusFilter] = useState<string>('ACTIVE');
  const { id } = useParams<RouteParams>();
  const [search, setSearch] = useState<string>('');

  // Covers both causes of a locked account: a read-only membership, and an organization
  // whose trial has expired.
  const { canWrite } = useCanWrite();
  const isReadOnly = !canWrite;
  const { role: userRole } = useAppSelector(
    (state: RootState) => state.user,
  );
  const canAddSeats = userRole === 'ADMIN' || userRole === 'OWNER';
  const isOwner = userRole === 'OWNER' || userRole === 'SUPER_ADMIN';

  // Only the roles this user outranks (peers included). Everything downstream —
  // tabs, the selected tab, the role dropdown — derives from this.
  const visibleRoleTabs = useMemo(
    () => getVisibleUserRoleTabs(userRole),
    [userRole],
  );

  // Lazy init off the persisted role so the first render already shows a tab this
  // user is allowed to browse.
  const [userTypeSelected, setUserTypeSelected] = useState<string | null>(
    () => getVisibleUserRoleTabs(userRole)[0]?.id ?? null,
  );

  const updateStatusMutation = useUpdateUserOrganizationStatus();

  const { data = { employees: [], total: 0 } as any, isLoading } = useEmployees(
    {
      role: userTypeSelected,
      id,
      page,
      limit: 10,
      text: search,
      organizationRoleStatus: statusFilter,
      // Ask the server to hide users above this user's level, so search can't
      // surface them either.
      hierarchyScoped: true,
    },
  );

  const { data: subscription } = useSubscription();
  const { data: activeSeats = [] } = useActiveSeats();
  const { data: totalIncludedSeats } = useTotalIncludedSeats();

  // Use total from backend (BASE + ADDONs) or fallback to subscription includedSeats
  const totalSeats = totalIncludedSeats ?? subscription?.includedSeats ?? 0;
  // activeSeats already includes all billable users (including OWNER)
  const occupiedSeats = activeSeats.length;
  const availableSeats = totalSeats - occupiedSeats;

  const seatCountingRoles = subscription?.planSnapshot?.seatCountingRoles;

  // Group the sidebar roles by whether they occupy a seat under the current plan.
  // Fully data-driven: any role NOT in seatCountingRoles (e.g. Crew Lead, Employee)
  // falls under "Don't use a seat". Falls back to a flat list with no plan data.
  const sidebarItems = useMemo(() => {
    if (!seatCountingRoles) return visibleRoleTabs;
    const usesSeat = visibleRoleTabs.filter((r) =>
      seatCountingRoles.includes(r.id),
    );
    const noSeat = visibleRoleTabs.filter(
      (r) => !seatCountingRoles.includes(r.id),
    );
    const grouped: any[] = [];
    if (usesSeat.length) {
      grouped.push(
        { id: '__hdr_uses_seat', name: 'Uses a seat', isSectionHeader: true },
        ...usesSeat,
      );
    }
    if (noSeat.length) {
      grouped.push(
        { id: '__hdr_no_seat', name: "Don't use a seat", isSectionHeader: true },
        ...noSeat,
      );
    }
    return grouped;
  }, [seatCountingRoles, visibleRoleTabs]);

  // The persisted tab is a single global key that survives organization switches,
  // so a value stored while acting as an Admin must not stick for a Superintendent
  // in another org. Fall back to the first tab this user may browse.
  useEffect(() => {
    const allowed = visibleRoleTabs.map((r) => r.id);
    const stored = localStorage.getItem('userRole');
    const next = stored && allowed.includes(stored) ? stored : allowed[0] ?? null;
    if (next && next !== stored) localStorage.setItem('userRole', next);
    setUserTypeSelected(next);
  }, [visibleRoleTabs]);

  const handleMenuItemClick = (item: any) => {
    localStorage.setItem('userRole', item.id);
    setUserTypeSelected(item.id);
    setPage(1);
    setSearch('');
  };

  // A role only needs a seat if the plan counts it. When plan data is missing,
  // fall back to the previous (conservative) behavior of requiring a seat.
  const roleUsesSeat = seatCountingRoles
    ? seatCountingRoles.includes(userTypeSelected ?? '')
    : true;

  const toggleModal = () => {
    // If trying to open the modal (not close it)
    if (!showNewEmployeeModal) {
      // Only gate on seats for roles that actually consume one (e.g. Admin) —
      // seatless roles like Crew Lead / Employee / Reviewer must never be blocked.
      if (!initialData && roleUsesSeat && availableSeats <= 0) {
        // Sprint 17, story-082: OWNER/ADMIN go into the Add Seats flow (the same
        // AddAddonModal, now branching internally on getLinkedAddOnsForOrganization); every
        // other role gets an inline message instead — no modal mounts, no plans network call.
        if (canAddSeats) {
          setShowNoSeatsModal(true);
        } else {
          toast.info(tActionMessages.askAdminToAddSeats);
        }
        return;
      }
    }
    setShowNewEmployeeModal(!showNewEmployeeModal);
    if (showNewEmployeeModal) setInitialData(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(1);
    setUserTypeSelected(null);
  };

  const handleStatusFilterChange = (option: any) => {
    setStatusFilter(option?.value || 'ACTIVE');
    setPage(1);
  };

  const statusFilterOptions = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'DISABLED', label: 'Disabled' },
    { value: 'READ_ONLY', label: 'Read Only' },
  ];

  const handleEditUser = (employee: Employee) => {
    setInitialData(employee);
    setShowNewEmployeeModal(true);
  };

  const handleUserCreated = (role: string) => {
    // Navigate to the newly created user's role tab, as long as it is one this
    // user may browse.
    if (!visibleRoleTabs.some((r) => r.id === role)) return;
    localStorage.setItem('userRole', role);
    setUserTypeSelected(role);
    setPage(1);
  };

  const handleChangeStatus = (employee: Employee) => {
    setStatusEmployee(employee);
    setSelectedStatus(
      employee.user?.organizationRoleStatus ||
        UserOrganizationRoleStatus.ACTIVE,
    );
    setShowChangeStatusModal(true);
  };

  const handleStatusChange = (status: UserOrganizationRoleStatus) => {
    setSelectedStatus(status);
  };

  const handleConfirmStatusChange = () => {
    if (!statusEmployee?.user?.userId || !selectedStatus) return;

    updateStatusMutation.mutate(
      {
        userId: statusEmployee.user.userId,
        status: selectedStatus,
      },
      {
        onSuccess: () => {
          setShowChangeStatusModal(false);
          setStatusEmployee(null);
          setSelectedStatus(null);
        },
        onError: (error) => {
          // Sprint 20, story-093: a re-enable refused server-side because the org has no
          // free seat. Route to the same out-of-seats modal instead of the status modal's
          // own error path — the status modal stays open with "Active" still selected.
          if (isNoSeatsAvailableError(error)) {
            setSeatErrorInfo(getNoSeatsExtensions(error));
            setShowNoSeatsModal(true);
          }
        },
      },
    );
  };

  const handleSeatsExhausted = (
    info: { occupiedSeats: number; includedSeats: number } | null,
  ) => {
    setSeatErrorInfo(info);
    setShowNoSeatsModal(true);
  };

  const selectedItem = search !== '' ? undefined : userTypeSelected;

  return (
    <div className="flex flex-col h-full">
      <SecondarySidebar
        items={sidebarItems}
        addButtonLabel="Add User"
        toggleModal={toggleModal}
        selectedItem={selectedItem}
        handleMenuItemClick={handleMenuItemClick}
        disabled={isReadOnly}
      />

      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <AccountCircleOutlined
            sx={{ fontSize: 28 }}
            className="text-orange-400 dark:text-orange-300"
          />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Users
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <SeatsIndicator
            occupiedSeats={occupiedSeats}
            totalSeats={totalSeats}
            onClick={() => setShowSeatModal(true)}
          />

          <Input
            value={search}
            iconLeft={<Search />}
            onChange={handleSearchChange}
            // className="w-[300px] md:min-w-[450px]"
            placeholder="Search by name or email"
          />

          <div className="min-w-[150px]">
            <ReactSelect
              options={statusFilterOptions}
              value={statusFilterOptions.find((opt) => opt.value === statusFilter)}
              onChange={handleStatusFilterChange}
              isSearchable={false}
            />
          </div>

          {canAddSeats && (
            <Button
              onClick={() => setShowAddAddonModal(true)}
              className="whitespace-nowrap"
              disabled={isReadOnly}>
              Add Seats
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <Loading center size="xl" />
      ) : data?.employees && data?.employees?.length > 0 ? (
        <div className="flex flex-col flex-1 justify-between">
          <UserCard
            showRole={search !== ''}
            onEditUser={handleEditUser}
            onChangeStatus={handleChangeStatus}
            onSelectUser={(employee) => setHistoryEmployee(employee)}
            isOwner={isOwner}
            actorRole={userRole}
            isReadOnly={isReadOnly}
            users={data?.employees || []}
          />

          <div className="flex justify-end mt-auto pt-4 pb-4">
            <Pagination
              take={10}
              page={page}
              total={data?.total || 0}
              setPage={(page) => setPage(page)}
            />
          </div>
        </div>
      ) : (
        <EmptyData
          textButton={statusFilter !== 'ACTIVE' ? undefined : 'New User'}
          onClickButton={statusFilter !== 'ACTIVE' ? undefined : toggleModal}
          description={
            statusFilter === 'DISABLED'
              ? 'No Disabled Users'
              : statusFilter === 'READ_ONLY'
                ? 'No Read Only Users'
                : 'No Active Users Yet'
          }
          iconButton={
            statusFilter !== 'ACTIVE' ? undefined : <AddIcon sx={{ fontSize: '16px' }} />
          }
          fullHeight
          disabled={isReadOnly}
        />
      )}

      <CreateEditEmployeeModal
        role={userTypeSelected}
        initialData={initialData}
        toggleNewModal={toggleModal}
        showNewModal={showNewEmployeeModal}
        title={initialData ? 'Edit User' : 'New User'}
        onUserCreated={handleUserCreated}
        onSeatsExhausted={handleSeatsExhausted}
      />

      <AddAddonModal
        isOpen={showAddAddonModal}
        onClose={() => setShowAddAddonModal(false)}
      />

      <AddAddonModal
        isOpen={showNoSeatsModal}
        onClose={() => {
          setShowNoSeatsModal(false);
          setSeatErrorInfo(null);
        }}
        noSeatsAvailable
        occupiedSeats={seatErrorInfo?.occupiedSeats ?? occupiedSeats}
        includedSeats={seatErrorInfo?.includedSeats ?? totalSeats}
      />

      <Modal
        isOpen={showChangeStatusModal}
        onClose={() => {
          setShowChangeStatusModal(false);
          setStatusEmployee(null);
          setSelectedStatus(null);
        }}
        title="Change User Status"
        confirmText="Update Status"
        handleConfirm={handleConfirmStatusChange}
        isLoading={updateStatusMutation.isPending}
        size="small">
        <ChangeUserStatusModal
          user={statusEmployee?.user}
          currentStatus={selectedStatus || undefined}
          onStatusChange={handleStatusChange}
        />
      </Modal>

      <UserPunchHistoryDrawer
        isOpen={!!historyEmployee}
        onClose={() => setHistoryEmployee(null)}
        employee={historyEmployee}
      />

      <SeatUsageModal
        isOpen={showSeatModal}
        onClose={() => setShowSeatModal(false)}
        occupiedSeats={occupiedSeats}
        totalSeats={totalSeats}
        seatCountingRoles={seatCountingRoles}
      />
    </div>
  );
};

export default UsersPage;
